// AI Bookkeeping Extractor — Handles bank statements, receipts, payment records via AI
import { supabase } from "@/integrations/supabase/client";
import { ExtractedInvoiceData } from "./invoiceExtractor";

export type BookkeepingDocType = "bank-statement" | "invoice" | "receipt" | "payment-record";

export interface BankTransaction {
  id: string;
  date: string;
  description: string;
  partyName?: string;
  amount: number;
  type: "debit" | "credit";
  category: string;
  gstEstimate?: number;
  suggestedDebitLedger: string;
  suggestedDebitGroup: string;
  suggestedCreditLedger: string;
  suggestedCreditGroup: string;
  narration: string;
  confidence: number;
  status: "pending" | "approved" | "rejected" | "modified";
  fileName: string;
}

export interface BookkeepingResult {
  docType: BookkeepingDocType;
  fileName: string;
  bankName?: string;
  accountNumber?: string;
  statementPeriod?: string;
  transactions: BankTransaction[];
  invoiceEntry?: ExtractedInvoiceData;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string || "");
    reader.onerror = () => resolve("");
    reader.readAsText(file);
  });
}

function detectDocType(file: File): BookkeepingDocType {
  const name = file.name.toLowerCase();
  if (name.includes("statement") || name.includes("bank") || file.type === "text/csv" || name.endsWith(".csv") || name.endsWith(".xls") || name.endsWith(".xlsx")) {
    return "bank-statement";
  }
  if (name.includes("receipt") || name.includes("bill") || name.includes("slip")) {
    return "receipt";
  }
  if (name.includes("payment") || name.includes("challan") || name.includes("remittance") || name.includes("neft") || name.includes("rtgs")) {
    return "payment-record";
  }
  return "invoice";
}

async function extractBankStatement(file: File): Promise<BookkeepingResult | null> {
  try {
    const content = await readFileAsText(file);
    const { data, error } = await supabase.functions.invoke("auto-bookkeeping", {
      body: { documentType: "bank-statement", fileName: file.name, fileContent: content.substring(0, 4000) },
    });

    if (error || data?.error) {
      console.error("Bank statement AI error:", error || data?.error);
      return null;
    }

    const extracted = data?.data;
    if (!extracted?.transactions) return null;

    const transactions: BankTransaction[] = extracted.transactions.map((t: any, idx: number) => ({
      id: `bt-${crypto.randomUUID().slice(0, 8)}`,
      date: t.date,
      description: t.description,
      partyName: t.partyName,
      amount: t.amount,
      type: t.type,
      category: t.category,
      gstEstimate: t.gstEstimate,
      suggestedDebitLedger: t.suggestedDebitLedger,
      suggestedDebitGroup: t.suggestedDebitGroup,
      suggestedCreditLedger: t.suggestedCreditLedger,
      suggestedCreditGroup: t.suggestedCreditGroup,
      narration: t.narration,
      confidence: t.confidence || 0.8,
      status: "pending" as const,
      fileName: file.name,
    }));

    return {
      docType: "bank-statement",
      fileName: file.name,
      bankName: extracted.bankName,
      accountNumber: extracted.accountNumber,
      statementPeriod: extracted.statementPeriod,
      transactions,
    };
  } catch (err) {
    console.error("Bank statement extraction failed:", err);
    return null;
  }
}

async function extractReceipt(file: File): Promise<BookkeepingResult | null> {
  try {
    const content = await readFileAsText(file);
    const { data, error } = await supabase.functions.invoke("auto-bookkeeping", {
      body: { documentType: "receipt", fileName: file.name, fileContent: content.substring(0, 3000) },
    });

    if (error || data?.error) return null;
    const e = data?.data;
    if (!e) return null;

    const tx: BankTransaction = {
      id: `rc-${crypto.randomUUID().slice(0, 8)}`,
      date: e.date,
      description: e.description,
      partyName: e.merchant,
      amount: e.totalAmount,
      type: "debit",
      category: e.category,
      gstEstimate: e.totalGST,
      suggestedDebitLedger: e.suggestedDebitLedger,
      suggestedDebitGroup: e.suggestedDebitGroup,
      suggestedCreditLedger: e.suggestedCreditLedger,
      suggestedCreditGroup: e.suggestedCreditGroup,
      narration: e.narration,
      confidence: e.confidence || 0.8,
      status: "pending",
      fileName: file.name,
    };

    return { docType: "receipt", fileName: file.name, transactions: [tx] };
  } catch { return null; }
}

async function extractPaymentRecord(file: File): Promise<BookkeepingResult | null> {
  try {
    const content = await readFileAsText(file);
    const { data, error } = await supabase.functions.invoke("auto-bookkeeping", {
      body: { documentType: "payment-record", fileName: file.name, fileContent: content.substring(0, 3000) },
    });

    if (error || data?.error) return null;
    const e = data?.data;
    if (!e) return null;

    const tx: BankTransaction = {
      id: `pm-${crypto.randomUUID().slice(0, 8)}`,
      date: e.date,
      description: e.purpose,
      partyName: e.payee,
      amount: e.amount,
      type: "debit",
      category: e.category,
      gstEstimate: e.gstAmount,
      suggestedDebitLedger: e.suggestedDebitLedger,
      suggestedDebitGroup: e.suggestedDebitGroup,
      suggestedCreditLedger: e.suggestedCreditLedger,
      suggestedCreditGroup: e.suggestedCreditGroup,
      narration: e.narration,
      confidence: e.confidence || 0.75,
      status: "pending",
      fileName: file.name,
    };

    return { docType: "payment-record", fileName: file.name, transactions: [tx] };
  } catch { return null; }
}

// Deterministic CSV statement parser
function parseCSVStatement(file: File, content: string): BookkeepingResult {
  const lines = content.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length <= 1) {
    return { docType: "bank-statement", fileName: file.name, transactions: [] };
  }

  const header = lines[0].toLowerCase().split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  const dateIdx = header.findIndex((h) => h.includes("date"));
  const descIdx = header.findIndex((h) => h.includes("desc") || h.includes("particular") || h.includes("narration"));
  const debitIdx = header.findIndex((h) => h.includes("debit") || h.includes("dr") || h.includes("withdrawal"));
  const creditIdx = header.findIndex((h) => h.includes("credit") || h.includes("cr") || h.includes("deposit"));
  const amountIdx = header.findIndex((h) => h.includes("amount"));

  const transactions: BankTransaction[] = [];
  for (let i = 1; i < lines.length; i++) {
    const row = lines[i].split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    if (row.length < 2) continue;
    const dateStr = dateIdx !== -1 && row[dateIdx] ? row[dateIdx] : new Date().toISOString().split("T")[0];
    const desc = descIdx !== -1 && row[descIdx] ? row[descIdx] : row[1] || "Transaction";
    let type: "debit" | "credit" = "debit";
    let amount = 0;

    if (debitIdx !== -1 && creditIdx !== -1) {
      const debitVal = parseFloat(row[debitIdx]?.replace(/[^0-9.]/g, "") || "0");
      const creditVal = parseFloat(row[creditIdx]?.replace(/[^0-9.]/g, "") || "0");
      if (creditVal > 0) {
        type = "credit";
        amount = creditVal;
      } else {
        type = "debit";
        amount = debitVal;
      }
    } else if (amountIdx !== -1) {
      amount = parseFloat(row[amountIdx]?.replace(/[^0-9.-]/g, "") || "0");
      if (amount < 0) {
        amount = Math.abs(amount);
        type = "debit";
      }
    }

    if (amount > 0) {
      transactions.push({
        id: `bt-${crypto.randomUUID().slice(0, 8)}`,
        date: dateStr,
        description: desc,
        partyName: desc.slice(0, 30),
        amount,
        type,
        category: type === "credit" ? "Customer Receipt" : "Expense",
        suggestedDebitLedger: type === "credit" ? "Bank Account" : "Office Supplies",
        suggestedDebitGroup: type === "credit" ? "Bank Accounts" : "Indirect Expenses",
        suggestedCreditLedger: type === "credit" ? desc.slice(0, 30) : "Bank Account",
        suggestedCreditGroup: type === "credit" ? "Sundry Debtors" : "Bank Accounts",
        narration: desc,
        confidence: 0.85,
        status: "pending",
        fileName: file.name,
      });
    }
  }

  return {
    docType: "bank-statement",
    fileName: file.name,
    transactions,
  };
}

export async function processBookkeepingDocument(file: File, forceType?: BookkeepingDocType): Promise<BookkeepingResult> {
  const docType = forceType || detectDocType(file);

  // If CSV or statement file, parse actual content
  if (file.name.endsWith(".csv") || file.type === "text/csv") {
    try {
      const content = await readFileAsText(file);
      const parsed = parseCSVStatement(file, content);
      if (parsed.transactions.length > 0) return parsed;
    } catch {
      // continue to AI
    }
  }

  // Try AI
  let result: BookkeepingResult | null = null;
  try {
    if (docType === "bank-statement") result = await extractBankStatement(file);
    else if (docType === "receipt") result = await extractReceipt(file);
    else if (docType === "payment-record") result = await extractPaymentRecord(file);
  } catch {
    // ignore
  }

  if (result && result.transactions.length > 0) return result;

  // Return clean empty result without fabricating fake transactions
  return {
    docType,
    fileName: file.name,
    transactions: [],
  };
}
